import {
  ONBOARDING_FINAL_STEP,
  ONBOARDING_FIRST_PLAYBOOK_STEP,
  ONBOARDING_UNLOCK_STEP,
  type MeResponse,
  type OnboardingState,
  type OnboardingStepNumber,
  type OnboardingStepState,
} from '@doneverse/shared'

/**
 * How the tracker renders. Derived PURELY from server state so a reload always
 * lands in the same mode — the client never remembers which mode it was in.
 *
 *   hidden    — no onboarding for this user (non-founder, grandfathered, or
 *               outside the `founder-onboarding` rollout — the dashboard's
 *               useOnboarding folds that flag in before calling selectOnboarding)
 *   locked    — steps 1–4: the panel leads every page and the page content
 *               beneath it is dimmed and inert until the unlock
 *   unlocked  — steps 6–11: the same panel, now non-blocking, over a live page
 *   minimized — everything done: a re-openable widget
 */
export type OnboardingTrackerMode = 'hidden' | 'locked' | 'unlocked' | 'minimized'

/**
 * The modes that actually draw something.
 *
 * Named here rather than re-spelled at each consumer so a new mode fails to
 * compile at every piece of mode-keyed copy at once — the panel's headline and
 * its foot note are two such tables, and a mode added to only one of them would
 * render a card with no title rather than an error.
 */
export type OnboardingVisibleMode = Exclude<OnboardingTrackerMode, 'hidden'>

/**
 * The steps the tracker draws, in rail order.
 *
 * Step 5 is excluded: it is the unlock boundary, not something the founder
 * does, and showing "Dashboard unlocked" as a checklist item next to nine real
 * tasks reads as busywork. Everything else — including the locked steps ahead —
 * is shown, because the point of the tracker is to make the whole journey
 * visible.
 *
 * This is also what "step N of 10" counts: the founder-facing number is a
 * position in THIS list, not the underlying `stepNumber`, so the tour is step 4
 * of 10 even though it is step 4 of an eleven-step graph.
 */
export function onboardingTrackerSteps(state: OnboardingState | null | undefined): OnboardingStepState[] {
  return (state?.steps ?? []).filter((s) => s.stepNumber !== ONBOARDING_UNLOCK_STEP)
}

/** Completed count / total across the tracker's steps — the progress readout. */
export function onboardingProgress(state: OnboardingState | null | undefined): {
  completed: number
  total: number
} {
  const steps = onboardingTrackerSteps(state)
  return {
    completed: steps.filter((s) => s.status === 'complete').length,
    total: steps.length,
  }
}

export function onboardingStep(
  state: OnboardingState | null | undefined,
  stepNumber: OnboardingStepNumber,
): OnboardingStepState | null {
  return (state?.steps ?? []).find((s) => s.stepNumber === stepNumber) ?? null
}

/**
 * Whether /playbooks should run in onboarding mode: step 7 — brief your first
 * playbook — is still open, so the library is narrowed to the starter set and
 * the page points at Coach Grace.
 *
 * Keyed on step 7's own status rather than on the tracker's mode, because 6/7/8/9
 * are simultaneously active and independent: a founder who has finished the
 * course but not briefed a playbook is still in this mode, and one who briefed
 * a playbook first gets their whole library back while the rest of onboarding
 * runs on.
 *
 * `null` state covers everyone with no tracker at all — non-founders, the
 * grandfathered cohort, and anyone outside the `founder-onboarding` rollout
 * (the dashboard's useOnboarding folds that flag in) — so they are never
 * filtered.
 */
export function isFirstPlaybookStepOpen(state: OnboardingState | null | undefined): boolean {
  const step = onboardingStep(state, ONBOARDING_FIRST_PLAYBOOK_STEP)
  return step !== null && step.status !== 'complete'
}

/**
 * Step 7 is DONE — the founder has briefed their first playbook.
 *
 * Deliberately not `!isFirstPlaybookStepOpen(state)`: everyone with no tracker
 * at all lands on `false` for both, which is the honest answer for each. They
 * have no first-playbook step to be open, and none to have finished either, so
 * a page must not tell them "brief sent, carry on with onboarding" for a
 * journey they were never on.
 */
export function isFirstPlaybookStepComplete(state: OnboardingState | null | undefined): boolean {
  return onboardingStep(state, ONBOARDING_FIRST_PLAYBOOK_STEP)?.status === 'complete'
}

/**
 * The tracker's mode.
 *
 * `minimized` is keyed on step 11 rather than "everything complete" because
 * step 10 can still be locked behind a launch date the founder has not reached
 * — that founder is finished with onboarding as far as the journey goes.
 */
export function onboardingTrackerMode(state: OnboardingState | null | undefined): OnboardingTrackerMode {
  if (!state) return 'hidden'
  if (!state.unlocked) return 'locked'
  const final = onboardingStep(state, ONBOARDING_FINAL_STEP)
  return final?.status === 'complete' ? 'minimized' : 'unlocked'
}

export interface OnboardingView {
  /**
   * Server state, or null when this user has no onboarding at all. Read
   * `state.unlocked` / `state.activeSteps` directly — this view adds only what
   * the server does not already send.
   */
  state: OnboardingState | null
  /** Lowest active step — where the panel opens. */
  nextUp: OnboardingStepNumber | null
  /** The steps the rail draws (step 5, the unlock boundary, is excluded). */
  trackerSteps: OnboardingStepState[]
  progress: { completed: number; total: number }
  mode: OnboardingTrackerMode
}

/**
 * Everything the tracker needs, derived from the profile the app shell already
 * holds.
 *
 * A pure function of `/v1/me` on purpose. Onboarding state rides on the profile
 * response, so the tracker must read the SAME cached query the shell fetched —
 * a hook of its own would key that query differently (the shell keys it per
 * user id) and fire a second bootstrap request on every page. Non-founders and
 * the grandfathered cohort get no `onboarding` block at all, so every selector
 * lands on the "nothing to show" branch.
 */
export function selectOnboarding(profile: Pick<MeResponse, 'onboarding'> | null | undefined): OnboardingView {
  const state = profile?.onboarding ?? null
  return {
    state,
    nextUp: state?.nextUp ?? null,
    trackerSteps: onboardingTrackerSteps(state),
    progress: onboardingProgress(state),
    mode: onboardingTrackerMode(state),
  }
}
